import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Button,
  Input,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  SortDescriptor,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DefaultLayout from "@/layouts/default";
import { booksAPI } from "@/services/api";
import { ChevronDownIcon, SearchIcon } from "@/components/icons";
import DataTable, { Column } from "@/components/DataTable";
import { useDirectionLanguage } from "@/components/DirectionLanguageContext";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Import custom DatePicker styles
import "./datepicker-custom.css";

// Define base API URL
const API_URL = import.meta.env.VITE_API_URL;

interface Book {
  id: string;
  name: string;
  author?: string;
  category?: string;
  stock?: number;
  price?: number;
}

// Enhanced interface for book report data
interface BookReportItem {
  bookId: string;
  bookName: string;
  totalPurchased: number;
  purchaseCost: number;
  totalSold: number;
  salesRevenue: number;
  currentStock: number;
  profit: number;
  transactions: number;
}

const AuditPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRtl } = useDirectionLanguage();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedBook, setSelectedBook] = useState<string>("");
  const [selectedBookName, setSelectedBookName] = useState<string>("");
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "profit",
    direction: "descending",
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Enhanced summary statistics for books
  const [summaryData, setSummaryData] = useState({
    totalBooks: 0,
    totalPurchasedCost: 0,
    totalSold: 0,
    totalRevenue: 0,
    totalCost: 0,
    totalProfit: 0,
  });

  // Book report data
  const [bookReportData, setBookReportData] = useState<BookReportItem[]>([]);

  // Fetch books for dropdown
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setApiError(null);
        const response = await booksAPI.getAll();
        setBooks(response || []);
      } catch (error) {
        console.error("Error fetching books:", error);
        setApiError(
          t("ErrorFetchingBooks", "Error fetching books. Please try again.")
        );
      }
    };

    fetchBooks();
  }, [t]);

  // Generate audit report focused on books
  const generateReport = async () => {
    if (!startDate || !endDate) {
      alert(t("PleaseSelectDateRange", "Please select a date range"));
      return;
    }

    setIsLoading(true);
    setApiError(null);

    // Use the same function as daily/weekly/monthly reports for consistency
    await generateReportWithDates(startDate, endDate);
  };

  // Helper functions for preset date ranges
  const generateDailyReport = async () => {
    const today = new Date();
    setStartDate(today);
    setEndDate(today);

    // Wait for state to update then generate report
    setTimeout(async () => {
      setIsLoading(true);
      setApiError(null);
      await generateReportWithDates(today, today);
    }, 100);
  };

  const generateWeeklyReport = async () => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);

    setStartDate(weekAgo);
    setEndDate(today);

    // Wait for state to update then generate report
    setTimeout(async () => {
      setIsLoading(true);
      setApiError(null);
      await generateReportWithDates(weekAgo, today);
    }, 100);
  };

  const generateMonthlyReport = async () => {
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(today.getDate() - 30);

    setStartDate(monthAgo);
    setEndDate(today);

    // Wait for state to update then generate report
    setTimeout(async () => {
      setIsLoading(true);
      setApiError(null);
      await generateReportWithDates(monthAgo, today);
    }, 100);
  };

  // Helper function to generate report with specific dates
  const generateReportWithDates = async (
    startDateParam: Date,
    endDateParam: Date
  ) => {
    try {
      // Format dates for API requests
      const formattedStartDate = new Date(
        startDateParam.getTime() - startDateParam.getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
      const formattedEndDate = new Date(
        endDateParam.getTime() - endDateParam.getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];

      // Get token from localStorage
      const token = localStorage.getItem("token");
      const clientId = localStorage.getItem("clientId");

      // Prepare API parameters
      const params: Record<string, string> = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };

      if (selectedBook) {
        params.book_id = selectedBook;
      }

      if (clientId) {
        params.clientId = clientId;
      }

      // Using axios for more consistent error handling
      const axiosConfig = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        params,
      };

      // Use the audit endpoint to get comprehensive book data
      try {
        const auditResponse = await axios.get(
          `${API_URL}/api/audit/report`,
          axiosConfig
        );

        if (!auditResponse.data) {
          throw new Error("No data received from audit API");
        }

        const { salesData = [], inventoryData = [] } = auditResponse.data;

        // Create a comprehensive map to store book data with all transactions
        const bookMap = new Map<string, BookReportItem>();

        // First, initialize all unique book names from the books list to ensure all books are shown
        // Group by book name instead of ID to combine versions
        const uniqueBookNames = new Set<string>();
        books.forEach((book) => {
          uniqueBookNames.add(book.name);
        });

        // Initialize map with unique book names and get current stock from books table
        // Note: booksAPI.getAll() returns aggregated books (stock already summed across versions)
        uniqueBookNames.forEach((bookName) => {
          // Get stock from first book with this name (API returns aggregated stock)
          const book = books.find((book) => book.name === bookName);
          const currentStock = book ? (Number(book.stock) || 0) : 0;

          bookMap.set(bookName, {
            bookId: bookName, // Use book name as key
            bookName: bookName,
            totalPurchased: 0, // This will be total cost from inventory
            purchaseCost: 0,
            totalSold: 0,
            salesRevenue: 0,
            currentStock: currentStock, // Set from actual books table (already aggregated)
            profit: 0,
            transactions: 0,
          });
        });

        // Process inventory data first to get purchase information
        (inventoryData as any[]).forEach((inv, index) => {
          // Try multiple ways to get book name (prioritize name over ID)
          const bookName =
            inv.book?.name ||
            inv.book_name ||
            inv.name ||
            `Book ${inv.book?.id || inv.book_id || inv.id || index}`;

          if (!bookMap.has(bookName)) {
            // Get stock from first book with this name (API returns aggregated stock)
            const book = books.find((book) => book.name === bookName);
            const currentStock = book ? (Number(book.stock) || 0) : 0;

            bookMap.set(bookName, {
              bookId: bookName, // Use book name as key
              bookName: bookName,
              totalPurchased: 0, // This will be total cost from inventory
              purchaseCost: 0,
              totalSold: 0,
              salesRevenue: 0,
              currentStock: currentStock, // Set from actual books table (already aggregated)
              profit: 0,
              transactions: 0,
            });
          }

          const bookData = bookMap.get(bookName)!;

          // Calculate total cost for this inventory transaction
          // User wants to use the price column from inventory table specifically
          const inventoryCost = Number(inv.price || 0); // Use price field first as user requested

          // Always store the latest purchase cost
          if (inventoryCost > 0) {
            bookData.purchaseCost = inventoryCost;
          }

          // totalPurchased now represents total cost/price of inventory purchases
          bookData.totalPurchased += inventoryCost;
          bookData.transactions += 1;

          // Note: currentStock is set from books table, not calculated from transactions
        });

        // If we still don't have purchase costs, try to get them from the books table
        books.forEach((book) => {
          if (bookMap.has(book.name)) {
            const bookData = bookMap.get(book.name)!;
            if (bookData.purchaseCost === 0 && book.price) {
              bookData.purchaseCost = Number(book.price);
            }
          }
        });

        // Process sales data to get sales information
        (salesData as any[]).forEach((sale, saleIndex) => {
          // If sale has items, process each item
          if (sale.items && Array.isArray(sale.items)) {
            (sale.items as any[]).forEach((item: any, itemIndex) => {
              // Try multiple ways to get book name from sale items
              const saleItemBookName =
                item.book_name ||
                item.book?.name ||
                item.name ||
                `Book ${item.book?.id || item.book_id || item.id || itemIndex}`;

              if (!bookMap.has(saleItemBookName)) {
                // Get stock from first book with this name (API returns aggregated stock)
                const book = books.find((book) => book.name === saleItemBookName);
                const currentStock = book ? (Number(book.stock) || 0) : 0;

                bookMap.set(saleItemBookName, {
                  bookId: saleItemBookName, // Use book name as key
                  bookName: saleItemBookName,
                  totalPurchased: 0,
                  purchaseCost: 0,
                  totalSold: 0,
                  salesRevenue: 0,
                  currentStock: currentStock, // Set from actual books table
                  profit: 0,
                  transactions: 0,
                });
              }

              const bookData = bookMap.get(saleItemBookName)!;
              bookData.totalSold += Number(item.quantity || 0);
              bookData.salesRevenue += Number(
                item.subtotal || item.total_price || 0
              );
              bookData.transactions += 1;

              // Note: currentStock is set from books table, not calculated from transactions
            });
          } else {
            // Direct sale object

            // Try multiple ways to get book name from direct sale
            const directSaleBookName =
              sale.book_name ||
              sale.book?.name ||
              sale.name ||
              `Book ${sale.book?.id || sale.book_id || sale.id || saleIndex}`;

            if (!bookMap.has(directSaleBookName)) {
              // Get stock from first book with this name (API returns aggregated stock)
              const book = books.find((book) => book.name === directSaleBookName);
              const currentStock = book ? (Number(book.stock) || 0) : 0;

              bookMap.set(directSaleBookName, {
                bookId: directSaleBookName, // Use book name as key
                bookName: directSaleBookName,
                totalPurchased: 0,
                purchaseCost: 0,
                totalSold: 0,
                salesRevenue: 0,
                currentStock: currentStock, // Set from actual books table
                profit: 0,
                transactions: 0,
              });
            }

            const bookData = bookMap.get(directSaleBookName)!;
            bookData.totalSold += Number(sale.quantity || 0);
            bookData.salesRevenue += Number(sale.total_price || 0);
            bookData.transactions += 1;

            // Note: currentStock is set from books table, not calculated from transactions
          }
        });

        // Calculate profit and profit margin for each book
        const bookReport = Array.from(bookMap.values())
          .filter(book => Number(book.totalSold) > 0) // Only include books with sales
          .map((book, index) => {
          const costPerBook = Number(book.purchaseCost) || 0;
          const quantitySold = Number(book.totalSold) || 0;
          const totalCostForSold = costPerBook * quantitySold;
          const profit = book.salesRevenue - totalCostForSold;

          return {
            ...book,
            // Ensure unique ID for React keys - use book name with index as fallback
            bookId: book.bookName || `book-${index}-${Date.now()}`,
            profit,
            // currentStock remains as set from books table
          };
        });

        // Sort by profit (highest first)
        bookReport.sort((a, b) => b.profit - a.profit);

        setBookReportData(bookReport);

        // Try to enrich book data with actual book names if they're missing or generic
        try {
          const booksNeedingEnrichment = bookReport.filter(
            (book) =>
              book.bookName.includes("Unknown Book") ||
              book.bookName.includes("Book ") ||
              book.bookName === book.bookId
          );

          if (booksNeedingEnrichment.length > 0 && books.length > 0) {
            const enrichedReport = bookReport.map((book) => {
              // Try to find the book in our books list
              const foundBook = books.find((b) =>
                b.name.toLowerCase().includes(book.bookName.toLowerCase())
              );

              if (foundBook) {
                return {
                  ...book,
                  bookName: foundBook.name,
                  bookId: foundBook.id,
                };
              }

              return book;
            });

            setBookReportData(enrichedReport);
          }
        } catch (enrichmentError) {
          // Silently fail enrichment
        }

        // Calculate comprehensive summary statistics
        const totalBooks = bookReport.length;
        const totalPurchasedCost = bookReport.reduce(
          (sum, book) => sum + book.totalPurchased,
          0
        ); // This is now cost
        const totalSold = bookReport.reduce(
          (sum, book) => sum + book.totalSold,
          0
        ); // This is quantity sold
        const totalRevenue = bookReport.reduce(
          (sum, book) => sum + book.salesRevenue,
          0
        );
        const totalCost = bookReport.reduce(
          (sum, book) => {
            // Get the cost per book directly from the inventory data
            const costPerBook = Number(book.purchaseCost) || 0;
            const quantitySold = Number(book.totalSold) || 0;
            return sum + (costPerBook * quantitySold);
          },
          0
        );
        const totalProfit = totalRevenue - totalCost;

        setSummaryData({
          totalBooks,
          totalPurchasedCost: totalPurchasedCost,
          totalSold,
          totalRevenue,
          totalCost,
          totalProfit,
        });

        // Only show error if no books exist at all, not just no transactions
        if (bookReport.length === 0 && books.length === 0) {
          setApiError(
            t(
              "NoDataForSelectedPeriod",
              "No book data found for the selected period"
            )
          );
        }
      } catch (error: any) {
        // Extract the error message from the response if it exists
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Unknown error";
        setApiError(
          t("ErrorGeneratingReport", "Error generating report: ") + errorMessage
        );

        // Clear any previous data
        setBookReportData([]);
      }
    } catch (error: any) {
      setApiError(
        t("ErrorGeneratingReport", "Error generating report: ") +
          (error.message || "Unknown error")
      );
      // Clear any previous data
      setBookReportData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedBook("");
    setSelectedBookName("");
    setSearchQuery("");
    setBookReportData([]);
    setApiError(null);
    setSummaryData({
      totalBooks: 0,
      totalPurchasedCost: 0,
      totalSold: 0,
      totalRevenue: 0,
      totalCost: 0,
      totalProfit: 0,
    });
  };

  // Filter book records based on search query
  const filteredBookRecords = bookReportData.filter(
    (record) =>
      record.bookName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.bookId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format currency
  const formatCurrency = (
    value: number | string | null | undefined
  ): string => {
    if (value === null || value === undefined || value === "") return "Rs0.00";

    // Convert string values to numbers
    const numericValue = typeof value === "string" ? parseFloat(value) : value;

    // Check if it's a valid number
    if (typeof numericValue !== "number" || isNaN(numericValue)) {
      return "Rs0.00";
    }

    return `Rs${numericValue.toFixed(2)}`;
  };

  // Handle sort change
  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  };

  // Book report table columns
  const bookColumns: Column<BookReportItem>[] = [
    {
      key: "bookName",
      label: t("BookName", "Book Name"),
      sortable: true,
    },
    {
      key: "totalSold",
      label: t("TotalSold", "Total Sold"),
      sortable: true,
    },
    {
      key: "currentStock",
      label: t("CurrentStock", "Current Stock"),
      sortable: true,
      renderCell: (item) => (
        <span
          className={item.currentStock <= 0 ? "text-danger" : "text-success"}
        >
          {item.currentStock}
        </span>
      ),
    },
    {
      key: "purchaseCost",
      label: t("PurchaseCost", "Purchase Cost"),
      sortable: true,
      renderCell: (item) => formatCurrency(item.purchaseCost),
    },
    {
      key: "salesRevenue",
      label: t("SalesRevenue", "Sales Revenue"),
      sortable: true,
      renderCell: (item) => formatCurrency(item.salesRevenue),
    },
    {
      key: "profit",
      label: t("Profit", "Profit"),
      sortable: true,
      renderCell: (item) => (
        <span className={item.profit >= 0 ? "text-success" : "text-danger"}>
          {formatCurrency(item.profit)}
        </span>
      ),
    },
  ];

  // Download PDF report function with RTL and Urdu support
  const downloadReport = async () => {
    if (!startDate || !endDate) {
      alert(t("PleaseSelectDateRange", "Please select a date range"));
      return;
    }

    if (bookReportData.length === 0) {
      alert(t("NoDataToDownload", "No data available to download. Please generate a report first."));
      return;
    }

    setIsDownloading(true);
    setApiError(null);
    let pdfContainer: HTMLDivElement | null = null;

    try {
      // Preload and wait for the Urdu font to be ready
      const fontUrl = '/fonts/Mehr_Nastaliq_Web v.2.0.woff';
      const fontName = 'Mehr Nastaliq';
      
      // Check if font is already loaded, if not, load it
      try {
        if (!document.fonts.check(`16px "${fontName}"`)) {
          const fontFace = new FontFace(fontName, `url(${fontUrl})`);
          await fontFace.load();
          document.fonts.add(fontFace);
          // Wait a bit more to ensure font is fully ready for rendering
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (fontError) {
        console.warn("Font loading error, continuing with fallback:", fontError);
      }

      // Format dates for filename
      const formattedStartDate = new Date(
        startDate.getTime() - startDate.getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
      const formattedEndDate = new Date(
        endDate.getTime() - endDate.getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];

      // Create a temporary container for the PDF content with RTL support
      pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.top = "0";
      pdfContainer.style.width = "210mm"; // A4 width
      pdfContainer.style.padding = "20mm";
      pdfContainer.style.backgroundColor = "white";
      pdfContainer.style.direction = "rtl";
      pdfContainer.style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
      pdfContainer.style.fontSize = "12px";
      pdfContainer.style.color = "#000";
      pdfContainer.dir = "rtl";

      // Add font-face style to the container with absolute URL
      const fontStyle = document.createElement("style");
      const baseUrl = window.location.origin;
      fontStyle.textContent = `
        @font-face {
          font-family: '${fontName}';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        @font-face {
          font-family: 'Mehr Nastaliq Web';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        * {
          font-family: "${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif !important;
        }
      `;
      pdfContainer.appendChild(fontStyle);

      // Helper function to create table HTML with header
      const createTableHTML = (rows: BookReportItem[], startIndex: number) => {
        const headerHTML = `
          <thead>
            <tr style="background-color: #4285c4; color: white;">
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("S.No", "نمبر")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("BookName", "کتاب کا نام")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalSold", "کل فروخت")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("CurrentStock", "موجودہ اسٹاک")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("PurchaseCost", "لاگت")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("SalesRevenue", "آمدنی")}</th>
              <th style="border: 1px solid #ddd; padding: 10px; text-align: right; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("Profit", "منافع")}</th>
            </tr>
          </thead>
        `;

        const rowsHTML = rows
          .map(
            (book, idx) => `
            <tr style="background-color: ${idx % 2 === 0 ? "#f9f9f9" : "white"};">
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${startIndex + idx + 1}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${book.bookName}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${book.totalSold}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${book.currentStock <= 0 ? "red" : "green"}; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${book.currentStock}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(book.purchaseCost)}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(book.salesRevenue)}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: ${book.profit >= 0 ? "green" : "red"}; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(book.profit)}</td>
            </tr>
          `
          )
          .join("");

        return `
          <table style="width: 100%; border-collapse: collapse; direction: rtl;">
            ${headerHTML}
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
        `;
      };

      // Create header HTML (title, dates, summary) - only for first page
      const now = new Date();
      const headerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 24px; margin-bottom: 15px; color: #333; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            ${t("BookInventoryReport", "کتابوں کا اسٹاک اور فروخت کی رپورٹ")}
          </h1>
          <p style="font-size: 14px; margin: 5px 0; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            <strong>${t("ReportPeriod", "رپورٹ کا دورانیہ")}:</strong> ${formattedStartDate} ${t("To", "سے")} ${formattedEndDate} ${t("To", "تک")}
          </p>
          <p style="font-size: 12px; margin: 5px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">
            ${t("GeneratedOn", "وقت اجراء رپورٹ")}: ${now.toLocaleDateString("ur-PK")} - ${now.toLocaleTimeString("ur-PK")}
          </p>
        </div>

        <div style="margin-bottom: 30px; display: flex; justify-content: space-around; flex-wrap: wrap; gap: 15px;">
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalBooks", "کل کتابیں")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${summaryData.totalBooks}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalSold", "کل فروخت")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${summaryData.totalSold}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalCost", "کل لاگت")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(summaryData.totalCost)}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalRevenue", "کل آمدنی")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(summaryData.totalRevenue)}</p>
          </div>
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; min-width: 120px; flex: 1;">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${t("TotalProfit", "کل منافع")}</h3>
            <p style="margin: 0; font-size: 18px; font-weight: bold; color: ${summaryData.totalProfit >= 0 ? "green" : "red"}; font-family: 'Mehr Nastaliq', 'Mehr Nastaliq Web', Arial, Tahoma, sans-serif;">${formatCurrency(summaryData.totalProfit)}</p>
          </div>
        </div>
      `;

      // Estimate rows per page (conservative estimate to ensure rows don't split)
      const rowsPerPageFirst = 15;
      const rowsPerPageSubsequent = 20;

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm

      // Split data into chunks and create pages
      const totalRows = bookReportData.length;
      let currentIndex = 0;
      let pageNumber = 0;

      while (currentIndex < totalRows) {
        const rowsPerPage =
          pageNumber === 0 ? rowsPerPageFirst : rowsPerPageSubsequent;
        const chunk = bookReportData.slice(
          currentIndex,
          currentIndex + rowsPerPage
        );

        // Create page content - header only on first page, table header on every page
        pdfContainer.innerHTML =
          pageNumber === 0
            ? headerHTML + createTableHTML(chunk, currentIndex)
            : `<div style="margin-bottom: 10px;"></div>` +
              createTableHTML(chunk, currentIndex); // Small margin for subsequent pages

        document.body.appendChild(pdfContainer);
        
        // Wait longer for font to render properly
        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
          const canvas = await html2canvas(pdfContainer, {
            scale: 1.5, // faster while keeping reasonable quality
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            windowWidth: pdfContainer.scrollWidth,
            windowHeight: pdfContainer.scrollHeight,
            removeContainer: false,
            onclone: (clonedDoc) => {
              // Ensure font is applied in the cloned document
              const clonedContainer = clonedDoc.querySelector('div[dir="rtl"]');
              if (clonedContainer) {
                (clonedContainer as HTMLElement).style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
              }
            },
          });

          document.body.removeChild(pdfContainer);

          // Add to PDF
          if (pageNumber > 0) {
            pdf.addPage();
          }

          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          const imgData = canvas.toDataURL("image/jpeg", 0.92);
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

          currentIndex += rowsPerPage;
          pageNumber++;
        } catch (canvasError) {
          if (pdfContainer && pdfContainer.parentNode) {
            document.body.removeChild(pdfContainer);
          }
          throw canvasError;
        }
      }

      // Generate filename
      const filename = `Audit_Report_${formattedStartDate}_to_${formattedEndDate}.pdf`;

      // Save the PDF
      try {
        pdf.save(filename);
      } catch (saveError) {
        console.error("Error saving PDF:", saveError);
        throw new Error("Failed to save PDF file");
      }
    } catch (error: any) {
      console.error("PDF generation error:", error);
      if (pdfContainer && pdfContainer.parentNode) {
        document.body.removeChild(pdfContainer);
      }
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to generate PDF report";
      setApiError(
        t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage
      );
      alert(
        t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <DefaultLayout>
      <div className="px-4 py-8">
        <h1 className="text-3xl font-bold text-center mb-8">
          {t("BookInventoryReport", "Book Inventory & Sales Report")}
        </h1>

        {/* Filter Controls */}
        <Card className="mb-6">
          <CardBody>
            <div className="flex flex-col gap-4">
              {/* Quick Report Buttons Row */}
              <div className="flex justify-center">
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button
                    color="success"
                    variant="flat"
                    onClick={generateDailyReport}
                    isLoading={isLoading}
                    size="md"
                    className="min-w-[120px]"
                  >
                    {t("DailyReport", "Daily")}
                  </Button>
                  <Button
                    color="warning"
                    variant="flat"
                    onClick={generateWeeklyReport}
                    isLoading={isLoading}
                    size="md"
                    className="min-w-[120px]"
                  >
                    {t("WeeklyReport", "Weekly")}
                  </Button>
                  <Button
                    color="secondary"
                    variant="flat"
                    onClick={generateMonthlyReport}
                    isLoading={isLoading}
                    size="md"
                    className="min-w-[120px]"
                  >
                    {t("MonthlyReport", "Monthly")}
                  </Button>
                </div>
              </div>

              {/* Date Selectors and Main Actions Row */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2 w-full">
                  <div className="flex flex-row items-center gap-2">
                    <div className="flex-none">
                      <DatePicker
                        selected={startDate}
                        onChange={(date: Date | null) => setStartDate(date)}
                        placeholderText={t("StartDate", "Start Date")}
                        maxDate={endDate || new Date()}
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        popperClassName="date-picker-popper"
                        popperPlacement="bottom-start"
                        withPortal
                      />
                    </div>
                    <div className="flex-none">
                      <DatePicker
                        selected={endDate}
                        onChange={(date: Date | null) => setEndDate(date)}
                        placeholderText={t("EndDate", "End Date")}
                        minDate={startDate ? startDate : undefined}
                        maxDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        popperClassName="date-picker-popper"
                        popperPlacement="bottom-start"
                        withPortal
                      />
                    </div>
                  </div>

                  <div className="w-full md:w-auto mt-4 md:mt-0">
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          variant="bordered"
                          className="w-full md:w-auto justify-between"
                          endContent={<ChevronDownIcon />}
                        >
                          {selectedBookName || t("AllBooks", "All Books")}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="Book selection"
                        className="max-h-64 overflow-y-auto"
                        items={books}
                      >
                        {(book) => (
                          <DropdownItem
                            key={book.id}
                            onClick={() => {
                              setSelectedBook(book.id);
                              setSelectedBookName(book.name);
                            }}
                          >
                            {book.name}
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>

                {/* Custom Report and Reset Buttons */}
                <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                  <Button
                    color="primary"
                    onClick={generateReport}
                    isLoading={isLoading}
                    className="min-w-[120px]"
                  >
                    {t("GenerateReport", "Generate Report")}
                  </Button>
                  <Button
                    color="success"
                    variant="bordered"
                    onClick={downloadReport}
                    isLoading={isDownloading}
                    className="min-w-[120px]"
                    title={t("DownloadReportTip", "رپورٹ فائل ڈاؤن لوڈ ہوگی")}
                  >
                    {t("DownloadReport", "Download PDF")}
                  </Button>
                  <Button
                    variant="bordered"
                    onClick={resetFilters}
                    className="min-w-[80px]"
                  >
                    {t("Reset", "Reset")}
                  </Button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* API Error Message */}
        {apiError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p>{apiError}</p>
          </div>
        )}

        {/* Enhanced Summary Cards */}
        {bookReportData.length > 0 && (
          <div className="flex justify-center mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-6xl w-full">
              <Card>
                <CardBody className="text-center py-4">
                  <p className="text-sm text-default-500">
                    {t("TotalCost", "Total Cost")}
                  </p>
                  <h3 className="text-2xl font-bold">
                    {formatCurrency(summaryData.totalCost)}
                  </h3>
                </CardBody>
              </Card>

              <Card>
                <CardBody className="text-center py-4">
                  <p className="text-sm text-default-500">
                    {t("TotalRevenue", "Total Revenue")}
                  </p>
                  <h3 className="text-2xl font-bold">
                    {formatCurrency(summaryData.totalRevenue)}
                  </h3>
                </CardBody>
              </Card>

              <Card>
                <CardBody className="text-center py-4">
                  <p className="text-sm text-default-500">
                    {t("TotalProfit", "Total Profit")}
                  </p>
                  <h3
                    className={`text-2xl font-bold ${summaryData.totalProfit >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {formatCurrency(summaryData.totalProfit)}
                  </h3>
                </CardBody>
              </Card>
            </div>
          </div>
        )}

        {/* Search */}
        {bookReportData.length > 0 && (
          <div className="flex justify-end items-center mb-4">
            <Input
              placeholder={t("SearchBooks", "Search books...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              startContent={<SearchIcon className="text-default-400" />}
              className="w-full md:w-64"
            />
          </div>
        )}

        {/* Book Report Data Table */}
        <div
          dir={isRtl ? "rtl" : "ltr"}
          className="overflow-x-hidden"
          style={{ maxWidth: "100%", overflowX: "hidden" }}
        >
          <div className="w-full overflow-x-auto" style={{ maxWidth: "100%" }}>
          <DataTable
            columns={bookColumns}
            data={filteredBookRecords}
            isLoading={isLoading}
            emptyContent={t(
              "NoBookData",
              "No book data available for the selected period"
            )}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
            addSerialNumbers={true}
            uniqueKey="bookId"
          />
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AuditPage;
